import {
  Badge,
  Button,
  Caption1,
  Combobox,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  Option,
  Spinner,
  Switch,
  tokens,
} from "@fluentui/react-components";
import {
  AddRegular,
  BuildingShopRegular,
  DeleteRegular,
  EditRegular,
  EyeOffRegular,
  EyeRegular,
} from "@fluentui/react-icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError } from "../api/client";
import { commerce, type Location } from "../api/commerce";
import { ConfirmDialog } from "../shell/ConfirmDialog";
import { baeTypography } from "../theme/baeTheme";
import { Page, PageHeader } from "../ui/PageHeader";
import { Card, CardHeader } from "../ui/Surface";
import { PHONE } from "../ui/tokens";

const useStyles = makeStyles({
  heading: { fontFamily: baeTypography.fontFamilyDisplay, letterSpacing: "-0.02em" },
  panel: {
    display: "grid",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    minWidth: 0,
    [PHONE]: { padding: tokens.spacingHorizontalM },
  },
  // auto-fit rather than fixed columns, so the row becomes a stack on a phone without a second
  // layout to keep true.
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(11rem, 1fr))",
    gap: tokens.spacingHorizontalM,
    alignItems: "end",
  },
  tree: { display: "grid", gap: tokens.spacingVerticalXXS, minWidth: 0 },
  node: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    minWidth: 0,
    ":hover": { backgroundColor: tokens.colorNeutralBackground3 },
  },
  // The code and the name read as one label and must not be split across a wrap.
  label: {
    display: "flex",
    alignItems: "baseline",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
    flexGrow: 1,
  },
  code: {
    fontFamily: baeTypography.fontFamilyMonospace,
    fontSize: "13px",
    minWidth: "6rem",
    [PHONE]: { minWidth: 0 },
  },
  name: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  actions: { display: "flex", gap: tokens.spacingHorizontalXS, flexWrap: "wrap" },
  dim: { opacity: 0.55 },
  muted: { color: tokens.colorNeutralForeground3 },
});

type Draft = {
  id: string;
  code: string;
  originalCode: string;
  name: string;
  isSite: boolean;
  isFulfillable: boolean;
  allocationPriority: number;
};

const draftFrom = (location: Location): Draft => ({
  id: location.id,
  code: location.code,
  originalCode: location.code,
  name: location.name,
  isSite: location.isSite,
  isFulfillable: location.isFulfillable,
  allocationPriority: location.allocationPriority,
});

/**
 * The location tree.
 *
 * A warehouse is one kind of location, so sites and bins are the same list at different depths —
 * which is exactly how it reads here, indented by depth rather than split into two screens.
 */
export function LocationsPage() {
  const styles = useStyles();
  const queryClient = useQueryClient();

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [template, setTemplate] = useState<"RetailStore" | "Warehouse" | "Empty">("RetailStore");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Location | null>(null);

  const locations = useQuery({ queryKey: ["locations"], queryFn: commerce.locations });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["locations"] });
    // Stock is reported per site, so a renamed or removed store changes what that screen says.
    void queryClient.invalidateQueries({ queryKey: ["stock"] });
  };

  const create = useMutation({
    mutationFn: () => commerce.createSiteFromTemplate(code.trim(), name.trim(), template),
    onSuccess: () => {
      setCode("");
      setName("");
      invalidate();
    },
  });

  const save = useMutation({
    mutationFn: (next: Draft) =>
      commerce.updateLocation(next.id, {
        code: next.code.trim(),
        name: next.name.trim(),
        isFulfillable: next.isFulfillable,
        allocationPriority: next.allocationPriority,
      }),
    onSuccess: () => {
      setDraft(null);
      invalidate();
    },
  });

  const toggle = useMutation({
    mutationFn: (location: Location) => commerce.setLocationActive(location.id, !location.isActive),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (location: Location) => commerce.deleteLocation(location.id),
    onSuccess: () => {
      setPendingDelete(null);
      invalidate();
    },
  });

  const sorted = [...(locations.data ?? [])].sort((a, b) => a.path.localeCompare(b.path));

  const listError = create.error ?? toggle.error;

  return (
    <Page width="medium">
      <PageHeader
        title="Locations"
        subtitle="Everywhere stock can be, from a store down to a single bin. A warehouse is one kind of location rather than a separate thing, which is why they are one list."
      />

      {listError instanceof ApiError && (
        <MessageBar intent="error">
          <MessageBarBody>{listError.message}</MessageBarBody>
        </MessageBar>
      )}

      <Card>
        <CardHeader
          icon={<BuildingShopRegular />}
          title="Open a store or warehouse"
          hint="A template gives every site the same shape, so a report that groups by location means the same thing across every branch."
        />

        <div className={styles.row}>
          <Field label="Code" required hint="Short. Scanned and typed.">
            <Input
              value={code}
              placeholder="LKO"
              onChange={(_, d) => setCode(d.value.toUpperCase())}
            />
          </Field>

          <Field label="Name" required>
            <Input value={name} placeholder="Lucknow store" onChange={(_, d) => setName(d.value)} />
          </Field>

          <Field label="Template">
            <Combobox
              value={template}
              onOptionSelect={(_, data) =>
                setTemplate((data.optionValue as typeof template) ?? "RetailStore")
              }
            >
              <Option value="RetailStore" text="RetailStore">
                Retail store — shop floor, stock room, goods in
              </Option>
              <Option value="Warehouse" text="Warehouse">
                Warehouse — goods in, zone, aisle, bins, staging
              </Option>
              <Option value="Empty" text="Empty">
                Empty — just the site
              </Option>
            </Combobox>
          </Field>

          <Button
            appearance="primary"
            icon={<AddRegular />}
            disabled={!code.trim() || !name.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? <Spinner size="tiny" /> : "Open"}
          </Button>
        </div>
      </Card>

      {draft && (
        <LocationEditor
          draft={draft}
          busy={save.isPending}
          error={save.error}
          onChange={setDraft}
          onCancel={() => setDraft(null)}
          onSave={() => save.mutate(draft)}
        />
      )}

      <Card>
        <CardHeader icon={<BuildingShopRegular />} title="The tree" />

        {locations.isLoading ? (
          <Spinner size="tiny" label="Loading" />
        ) : sorted.length === 0 ? (
          <Caption1 className={styles.muted}>No locations yet.</Caption1>
        ) : (
          <div className={styles.tree}>
            {sorted.map((location) => (
              <div
                key={location.id}
                className={`${styles.node} ${location.isActive ? "" : styles.dim}`}
                style={{ paddingLeft: `${location.depth * 1.25 + 0.5}rem` }}
              >
                <div className={styles.label}>
                  <span className={styles.code}>{location.code}</span>
                  <span className={styles.name}>{location.name}</span>
                </div>

                <Badge appearance="outline" size="small">
                  {location.kind}
                </Badge>
                {!location.isActive && (
                  <Badge appearance="tint" size="small">
                    Hidden
                  </Badge>
                )}
                {location.canHoldStock ? (
                  <Badge appearance="tint" color="success" size="small">
                    holds stock
                  </Badge>
                ) : (
                  <Caption1 className={styles.muted}>organises only</Caption1>
                )}
                {location.isSite && location.isFulfillable && (
                  <Badge appearance="tint" color="informative" size="small">
                    fulfils orders
                  </Badge>
                )}

                <div className={styles.actions}>
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={<EditRegular />}
                    aria-label={`Edit ${location.code}`}
                    onClick={() => setDraft(draftFrom(location))}
                  />
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={location.isActive ? <EyeOffRegular /> : <EyeRegular />}
                    aria-label={
                      location.isActive ? `Hide ${location.code}` : `Show ${location.code}`
                    }
                    onClick={() => toggle.mutate(location)}
                  />
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={<DeleteRegular />}
                    aria-label={`Delete ${location.code}`}
                    onClick={() => {
                      remove.reset();
                      setPendingDelete(location);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Caption1 className={styles.muted}>
        Hiding a location hides everything inside it and keeps its history. Deleting is only
        possible while no stock has ever been there — once the ledger mentions a place, removing it
        would leave that history pointing at nothing.
      </Caption1>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={`Delete ${pendingDelete?.code ?? ""}?`}
        confirmLabel="Delete it"
        busy={remove.isPending}
        error={remove.error}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete)}
      >
        <Caption1>
          {pendingDelete?.name} and everything inside it will be removed. This only works if no
          stock has ever been recorded there — otherwise nothing is deleted and you will be told
          why.
        </Caption1>
      </ConfirmDialog>
    </Page>
  );
}

function LocationEditor({
  draft,
  busy,
  error,
  onChange,
  onCancel,
  onSave,
}: {
  draft: Draft;
  busy: boolean;
  error: unknown;
  onChange: (next: Draft) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const styles = useStyles();
  const codeChanged = draft.code.trim().toUpperCase() !== draft.originalCode;

  return (
    <Card>
      <CardHeader icon={<EditRegular />} title={`Editing ${draft.originalCode}`} />

      {error instanceof ApiError && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Could not save</MessageBarTitle>
            {error.message}
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.row}>
        <Field label="Code" required hint="Scanned and typed. Unique across the business.">
          <Input
            value={draft.code}
            onChange={(_, d) => onChange({ ...draft, code: d.value.toUpperCase() })}
          />
        </Field>

        <Field label="Name" required>
          <Input value={draft.name} onChange={(_, d) => onChange({ ...draft, name: d.value })} />
        </Field>
      </div>

      {codeChanged && (
        <MessageBar intent="warning">
          <MessageBarBody>
            Labels already printed still say {draft.originalCode}. Everything inside this location
            is renumbered to match the new code.
          </MessageBarBody>
        </MessageBar>
      )}

      {draft.isSite && (
        <div className={styles.row}>
          <Field label="Orders" hint="Whether stock here can be sent to customers.">
            <Switch
              checked={draft.isFulfillable}
              label={draft.isFulfillable ? "Fulfils orders" : "Storage only"}
              onChange={(_, d) => onChange({ ...draft, isFulfillable: d.checked })}
            />
          </Field>

          <Field
            label="Ship from here first?"
            hint="Lower goes first when two stores both have it."
          >
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              value={String(draft.allocationPriority)}
              onChange={(_, d) => onChange({ ...draft, allocationPriority: Number(d.value) || 0 })}
            />
          </Field>
        </div>
      )}

      <div className={styles.actions}>
        <Button
          appearance="primary"
          disabled={busy || draft.code.trim() === "" || draft.name.trim() === ""}
          onClick={onSave}
        >
          {busy ? <Spinner size="tiny" /> : "Save location"}
        </Button>
        <Button appearance="subtle" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

export type { Location };
